import { ActionType, createReducer } from "typesafe-actions";
import { resetApiError } from "store/features/general";
import * as actions from "./annotations.actions";
import { Annotation } from "./interfaces";
import { Permission } from "api";

const {
    fetchAnnotations,
    submitAnnotations,
    setSelectedAnnotation,
    resetAnnotationsState,
    addAnnotation,
    updateAnnotations,
    submitIsAnnotationReviewed,
    removeAnnotation,
    attachAssetToAnnotation,
    removeAnnotationAsset,
} = actions;

export type AnnotationsAction = ActionType<typeof actions>;

export type AnnotationsState = Readonly<{
    adMaterialId: number | null;
    selectedAnnotation: Annotation | null;
    annotations: Annotation[];
    permissions: Permission[];
    hasError: boolean;
    isProcessing: boolean;
    newlyAddedAnnotations: number;
    isDirty: boolean;
}>;

export const annotationsState: AnnotationsState = {
    adMaterialId: null,
    selectedAnnotation: null,
    annotations: [],
    permissions: [],
    hasError: false,
    isProcessing: false,
    newlyAddedAnnotations: 0,
    isDirty: false,
};

export const annotationsReducer = createReducer<AnnotationsState, AnnotationsAction>(annotationsState)
    .handleAction(setSelectedAnnotation, (state, { payload }) => {
        return {
            ...state,
            selectedAnnotation: payload,
        };
    })
    .handleAction(resetAnnotationsState, (state) => {
        return {
            ...state,
            selectedAnnotation: null,
            annotations: [],
        };
    })
    // used for adding new annotations
    .handleAction(addAnnotation, (state, { payload }) => {
        return {
            ...state,
            selectedAnnotation: null,
            annotations: [...state.annotations, payload.annotation],
            newlyAddedAnnotations: state.newlyAddedAnnotations + 1,
            isDirty: true,
        };
    })
    // used for updating existing, or removing NEW annotations (for removing existing -> removeAnnotation)
    .handleAction(updateAnnotations, (state, { payload }) => {
        const newlyAddedAnnotations =
            state.annotations.length === payload.theAnnotations.length
                ? state.newlyAddedAnnotations
                : state.newlyAddedAnnotations - 1;
        return {
            ...state,
            annotations: [...payload.theAnnotations],
            newlyAddedAnnotations,
            isDirty: true,
        };
    })
    .handleAction(
        [
            fetchAnnotations.request,
            submitAnnotations.request,
            removeAnnotation.request,
            attachAssetToAnnotation.request,
            removeAnnotationAsset.request,
        ],
        (state) => {
            return {
                ...state,
                isProcessing: true,
            };
        }
    )
    .handleAction(fetchAnnotations.success, (state, { payload: { adMaterialId, annotations, permissions } }) => {
        return {
            ...state,
            isProcessing: false,
            adMaterialId,
            annotations,
            permissions,
            isDirty: false,
        };
    })
    .handleAction(submitAnnotations.success, (state, { payload: { annotations, permissions } }) => {
        return {
            ...state,
            annotations,
            permissions,
            isProcessing: false,
            isDirty: false,
        };
    })
    .handleAction(attachAssetToAnnotation.success as never, (state, { payload: { selectionId, fileName, id } }) => {
        return {
            ...state,
            annotations: state.annotations.map((annotation) => {
                if (annotation.selectionId === selectionId) {
                    return {
                        ...annotation,
                        modified: true,
                        attachedAsset: {
                            id,
                            fileName,
                        },
                    };
                }
                return annotation;
            }),
            isProcessing: false,
            isDirty: true,
        };
    })
    .handleAction(removeAnnotationAsset.success as never, (state, { payload: { selectionId } }) => {
        return {
            ...state,
            annotations: state.annotations.map((annotation) => {
                if (annotation.selectionId === selectionId) {
                    return {
                        ...annotation,
                        attachedAsset: undefined,
                    };
                }
                return annotation;
            }),
            isProcessing: false,
        };
    })
    .handleAction(removeAnnotation.success, (state) => {
        return {
            ...state,
            isProcessing: false,
        };
    })
    .handleAction(
        [
            fetchAnnotations.failure,
            submitAnnotations.failure,
            submitIsAnnotationReviewed.failure,
            removeAnnotation.failure,
            attachAssetToAnnotation.failure,
            removeAnnotationAsset.failure,
        ],
        (state) => {
            return {
                ...state,
                isProcessing: false,
                hasError: true,
            };
        }
    )
    .handleAction(resetApiError as never, (state) => {
        return {
            ...state,
            hasError: false,
        };
    });
